"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, Compass, Flame, Globe as Globe2, HeartHandshake, Users } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const milestones = [{
  year: "1914",
  title: "El Primer Concilio General",
  description: "Más de 300 delegados se reunieron en Hot Springs, Arkansas, estableciendo una confraternidad comprometida con la evangelización global."
}, {
  year: "1916",
  title: "Declaración de Verdades Fundamentales",
  description: "Adopción de verdades doctrinales esenciales para unificar a las congregaciones locales y preservar la enseñanza bíblica."
}, {
  year: "Hoy",
  title: "Confraternidad Global",
  description: "Un movimiento mundial que representa a decenas de millones de creyentes en más de 250 naciones y territorios."
}];
const missionPillars = [{
  title: "Evangelizar a los Perdidos",
  icon: Flame
}, {
  title: "Adorar a Dios",
  icon: Compass
}, {
  title: "Discipular a los Creyentes",
  icon: Users
}, {
  title: "Mostrar Compasión",
  icon: HeartHandshake
}];
export default function Founding() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="foundinghistory" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-8">
            <div className="space-y-4">
              <Badge variant="outline" className="gap-1.5 px-3 py-1 text-xs uppercase tracking-wider">
                <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                Fundada en Abril de 1914
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
                Un Movimiento Nacido para Alcanzar al Mundo
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                En abril de 1914, aproximadamente 300 delegados se reunieron en el Grand Opera House de Hot Springs, Arkansas, para organizar una confraternidad cooperativa. Unidos por un deseo apasionado de llevar el mensaje de Jesucristo a todas las naciones, sentaron las bases de lo que hoy son las Asambleas de Dios.
              </p>
            </div>

            <p className="text-muted-foreground leading-relaxed">
              Desde aquellos primeros comienzos, la confraternidad no se estableció como una jerarquía rígida, sino como una cooperación voluntaria entre asambleas independientes comprometidas con la rendición de cuentas mutua, la sana doctrina bíblica y el urgente alcance misionero.
            </p>

            <Separator />

            {}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-foreground">
                Marco de la Misión Cuádruple
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {missionPillars.map((pillar, i) => {
                const Icon = pillar.icon;
                return <div key={pillar.title} data-index={i} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card/60">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Icon className="size-5" />
                      </div>
                      <span className="font-medium text-card-foreground text-sm">
                        {pillar.title}
                      </span>
                    </div>;
              })}
              </div>
            </div>

            {}
            <div className="space-y-6 pt-2">
              <h3 className="text-xl font-semibold text-foreground">
                Hitos Históricos Clave
              </h3>
              <div className="space-y-4 border-l-2 border-border pl-4 sm:pl-6 ml-2">
                {milestones.map((item, i) => <div key={item.year} data-index={i} className="relative group">
                    <div className="absolute -left-[23px] sm:-left-[31px] top-1.5 h-3.5 w-3.5 rounded-full border-2 border-background bg-primary" />
                    <div className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">
                      {item.year}
                    </div>
                    <h4 className="text-base font-semibold text-foreground">
                      {item.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {item.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </motion.div>

          {}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.6,
          delay: 0.15,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-6">
            {}
            <Card className="overflow-hidden border border-border shadow-md">
              <div className="relative aspect-4/3 w-full bg-muted overflow-hidden">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1768199/img/history_since_1914.png" data-wvc-sizes="(max-width: 768px) 100vw, 500px" alt="Reunión histórica de los primeros delegados de las Asambleas de Dios" loading="lazy" decoding="async" className="h-full w-full object-cover grayscale contrast-110 hover:grayscale-0 transition-all duration-500" src="https://current-thrush.10web.cloud/wp-content/uploads/2026/08/Hot_Springs.jpg" />
                <div className="absolute top-3 right-3">
                  <Badge variant="secondary" className="shadow-xs backdrop-blur-md">
                    Registro de Archivo
                  </Badge>
                </div>
              </div>
              <CardContent className="pt-4 pb-5">
                <p className="text-xs font-medium text-muted-foreground italic">
                  Los primeros delegados se reúnen en Hot Springs, Arkansas (1914) para unir sus recursos en la labor misionera nacional y global.
                </p>
              </CardContent>
            </Card>

            {}
            <Card className="border border-border bg-card">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 text-primary mb-1">
                  <Globe2 className="size-5" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Nuestra Confraternidad
                  </span>
                </div>
                <CardTitle className="text-lg">
                  Alcance e Impacto Global
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-card-foreground">
                <div className="flex items-start justify-between border-b border-border/60 pb-3">
                  <span className="text-muted-foreground">Reunión Original</span>
                  <span className="font-semibold text-right">~300 Delegados (1914)</span>
                </div>
                <div className="flex items-start justify-between border-b border-border/60 pb-3">
                  <span className="text-muted-foreground">Base Doctrinal</span>
                  <span className="font-semibold text-right">16 Verdades Fundamentales</span>
                </div>
                <div className="flex items-start justify-between border-b border-border/60 pb-3">
                  <span className="text-muted-foreground">Estructura</span>
                  <span className="font-semibold text-right">Cooperativa Voluntaria</span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-muted-foreground">Enfoque</span>
                  <span className="font-semibold text-right">Evangelización y Compasión</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}